// Manager — Ongoing check-in (User Journeys & Service Design v2, Journey 04).
//
// The focused, low-effort destination for a due touch point: the manager
// arrives (from the reminder email or the case), sees which check-in is due,
// gets Maeve's 2-3 conversation pointers, has the conversation off-platform,
// then logs it in one place — "keeps the manager showing up without making it
// feel like admin." Deliberately NOT the dense case-detail page.
//
// Shares the `['cases', id]` query cache with the case-detail + first-response
// routes. No device guard — single-column, mobile-first, reachable from the
// mobile Maeve-First entry and the email deep-link.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CaseDetailWithRtw,
  CompleteTouchpointRequest,
  TouchpointSummary,
  TouchpointType,
} from '@besideu/shared/cases';
import { api, ApiError } from '@/lib/api.ts';
import { Card } from '@/components/ui/Card.tsx';
import { MaeveAvatar } from '@/components/brand/MaeveAvatar.tsx';
import { CaseNotFound } from '@/components/CaseNotFound.tsx';
import { ManagerMaeveTouchpointPrep } from '@/components/ManagerMaeveTouchpointPrep.tsx';
import { ManagerMaeveGuidance } from '@/components/ManagerMaeveGuidance.tsx';
import { TouchpointCompletionForm } from '@/components/CaseDetailView.tsx';

const TOUCHPOINT_LABELS: Record<TouchpointType, string> = {
  Day_3: 'Day 3',
  Week_1: 'Week 1',
  Pre_Return: 'Pre-return',
  Week_1_Back: 'Week 1 back',
  Month_1: 'Month 1',
  Month_3: 'Month 3',
  Month_6: 'Month 6',
  Month_9: 'Month 9',
  Month_12: 'Month 12',
  Anniversary: 'Anniversary',
};

// The touch point to check in on: the most-urgent still-actionable one
// (overdue first, then earliest scheduled). Excludes completed / suppressed /
// skipped — those are not a "due" check-in.
function pickFocus(touchpoints: TouchpointSummary[], excludeId?: string): TouchpointSummary | null {
  const reachable = touchpoints.filter(
    (t) => (t.status === 'Pending' || t.status === 'Overdue') && t.id !== excludeId,
  );
  if (reachable.length === 0) return null;
  return [...reachable].sort(
    (a, b) =>
      Number(b.isOverdue) - Number(a.isOverdue) || a.scheduledFor.localeCompare(b.scheduledFor),
  )[0]!;
}

export function ManagerCheckIn() {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [completed, setCompleted] = useState<TouchpointSummary | null>(null);

  // This route component isn't remounted by React Router on a `:id`-only
  // navigation (e.g. browser back/forward between two cases' check-in
  // pages), unlike the case-scoped Maeve components below it on this same
  // page — which get a `key={id}` from their caller to force that. There's
  // no external caller here to attach a key to (this component IS the
  // route), so the reset has to happen internally instead: without it, a
  // manager who just completed case A's touch point and navigated to case
  // B's check-in page would see the "Check-in logged" confirmation for
  // case B before they've done anything.
  useEffect(() => {
    setCompleted(null);
  }, [id]);

  const detail = useQuery({
    queryKey: ['cases', id],
    queryFn: () => api.get<CaseDetailWithRtw>(`/api/cases/${id}`),
    enabled: id !== '',
  });

  const complete = useMutation({
    mutationFn: (vars: { tp: TouchpointSummary; completion: CompleteTouchpointRequest }) =>
      api.post<{ touchpoint: TouchpointSummary }>(
        `/api/cases/${id}/touchpoints/${vars.tp.id}/done`,
        vars.completion,
      ),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ['cases', id] });
      setCompleted(vars.tp);
    },
  });

  if (detail.isPending) return <p className="text-muted-foreground">Loading…</p>;
  if (detail.error) {
    if (detail.error instanceof ApiError && detail.error.status === 404) {
      return (
        <section className="mx-auto max-w-2xl space-y-6">
          <CaseNotFound
            backTo="/manager/team"
            reason="It may belong to another manager, or it may have been removed."
          />
        </section>
      );
    }
    return <p className="text-traffic-red">Could not load this support pathway.</p>;
  }

  const c = detail.data.case;
  const touchpoints = detail.data.touchpoints;
  const focus = completed ? null : pickFocus(touchpoints);

  return (
    <section className="mx-auto max-w-2xl space-y-6">
      <Link
        to={`/manager/cases/${id}`}
        className="text-sm text-muted-foreground underline-offset-4 hover:underline"
      >
        ← Back to the support pathway
      </Link>

      {completed ? (
        <CheckInDone
          caseId={id}
          employeeName={c.employee.name}
          completed={completed}
          next={pickFocus(touchpoints, completed.id)}
        />
      ) : focus ? (
        (() => {
          // Journey 06 — the Pre_Return touch point IS the manager's return-prep
          // moment: reframe the strip and add a welcome-back message drafter.
          const isReturn = focus.type === 'Pre_Return';
          const firstName = c.employee.name.split(' ')[0] || c.employee.name;
          return (
            <>
              {/* Context strip — which check-in (or return prep), and when. */}
              <div className="flex items-center gap-4 rounded-2xl bg-ink px-5 py-4 text-white sm:px-6">
                <MaeveAvatar size={44} rings={false} />
                <div className="min-w-0">
                  <p className="text-[0.68rem] font-semibold tracking-[0.16em] text-primary uppercase">
                    {isReturn ? 'Return to work' : 'Check-in'}
                  </p>
                  <p className="mt-0.5 font-heading text-lg font-bold">
                    {isReturn
                      ? `Preparing ${c.employee.name}'s return`
                      : `Checking in with ${c.employee.name}`}
                  </p>
                  <p className="text-sm text-white/70">
                    {isReturn && c.returnToWorkDate
                      ? `Returns ${c.returnToWorkDate}`
                      : `${TOUCHPOINT_LABELS[focus.type]} · scheduled ${focus.scheduledFor}`}
                    {focus.isOverdue ? ' · overdue' : ''}
                  </p>
                </div>
              </div>

              {/* `key` forces a remount on caseId change — see the matching
                  comment in ManagerCaseDetail.tsx. Without it, navigating
                  between two cases' check-in pages (same route pattern, no
                  unmount) would carry the previous case's prep history and
                  pointers into the new case's briefing. Suffixed (not bare
                  `id`) because the welcome-back `ManagerMaeveGuidance` below
                  is a SIBLING under the same parent when `isReturn` is true —
                  identical keys among siblings triggers React's "duplicate
                  key" warning and puts reconciliation in unsupported
                  territory. */}
              <ManagerMaeveTouchpointPrep
                key={`touchpoint-prep-${id}`}
                caseId={id}
                touchpoints={touchpoints}
                preselectTouchpointId={focus.id}
              />

              {/* Journey 06 STEP 2 — draft the personal welcome-back message the
                  manager sends from their own account. Reuses the CGC-governed
                  Manager guidance endpoint, seeded with a welcome-back opener. */}
              {isReturn ? (
                // `key` forces a remount on caseId change — see the matching
                // comment in ManagerCaseDetail.tsx. Without it, navigating
                // between two cases' check-in pages (same route pattern, no
                // unmount) would carry the previous case's history and leave
                // the `seeded` ref stuck, so this case's `prefill` (with the
                // new employee's name) never seeds the draft. Suffixed to stay
                // distinct from the touchpoint-prep key above (same-parent
                // siblings when this branch renders).
                <ManagerMaeveGuidance
                  key={`guidance-${id}`}
                  threadId="check-in"
                  caseId={id}
                  title="Draft a welcome-back message"
                  subtitle="Maeve can draft a short, warm message for their first day — no work content, no pressure to reply. You send it from your own account; edit anything that doesn't feel right."
                  prefill={`Help me write a short, warm welcome-back message for ${firstName}'s first day back. Keep it human — no work content, and no pressure for them to reply.`}
                />
              ) : null}

              <Card as="section" className="space-y-3">
                <div>
                  <h2 className="text-base font-semibold">
                    {isReturn ? 'Log the pre-return check-in' : 'Log this check-in'}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Once you&rsquo;ve had the conversation, record what happened. Only a factual
                    outcome — never how they seemed.
                  </p>
                </div>
                {complete.error ? (
                  <p className="text-sm text-traffic-red" role="alert">
                    Could not log this check-in. Try again, or use the support pathway page.
                  </p>
                ) : null}
                <TouchpointCompletionForm
                  startOpen
                  pending={complete.isPending}
                  onSubmit={(completion) => complete.mutate({ tp: focus, completion })}
                />
              </Card>
            </>
          );
        })()
      ) : (
        <NoCheckInDue caseId={id} employeeName={c.employee.name} />
      )}
    </section>
  );
}

function CheckInDone({
  caseId,
  employeeName,
  completed,
  next,
}: {
  caseId: string;
  employeeName: string;
  completed: TouchpointSummary;
  next: TouchpointSummary | null;
}) {
  const firstName = employeeName.split(' ')[0] || employeeName;
  return (
    <Card className="space-y-4">
      <div>
        <h2 className="font-heading text-xl font-bold">Check-in logged.</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          The {TOUCHPOINT_LABELS[completed.type]} check-in with {firstName} is recorded. Showing up
          is what matters — you did.
        </p>
      </div>
      <p className="flex items-start gap-2 text-sm">
        <span aria-hidden className="mt-0.5 text-traffic-green-text">
          ✓
        </span>
        <span>
          {next ? (
            <>
              Your next check-in is{' '}
              <span className="font-medium text-foreground">{TOUCHPOINT_LABELS[next.type]}</span> on{' '}
              <span className="font-medium text-foreground">{next.scheduledFor}</span> — Maeve will
              remind you.
            </>
          ) : (
            <>No further check-ins are due right now. Maeve will remind you when the next one is.</>
          )}
        </span>
      </p>
      <div className="flex flex-wrap gap-2">
        <Link
          to={`/manager/cases/${caseId}`}
          className="inline-flex items-center rounded-sm border border-border px-4 py-2.5 text-sm font-medium hover:bg-muted"
        >
          Back to {firstName}&rsquo;s support pathway
        </Link>
        {/* `/manager/team`, not `/manager` — since PO 2026-08-06 the latter
            is the four action cards and the pathway list this promises lives
            on the Team tab. */}
        <Link
          to="/manager/team"
          className="inline-flex items-center rounded-sm border border-border px-4 py-2.5 text-sm font-medium hover:bg-muted"
        >
          Back to my team
        </Link>
      </div>
    </Card>
  );
}

function NoCheckInDue({ caseId, employeeName }: { caseId: string; employeeName: string }) {
  const firstName = employeeName.split(' ')[0] || employeeName;
  return (
    <Card className="space-y-3">
      <h2 className="font-heading text-xl font-bold">Nothing due right now</h2>
      <p className="text-sm text-muted-foreground">
        There&rsquo;s no check-in due for {firstName} at the moment. Maeve will remind you when the
        next one comes up — there&rsquo;s nothing you need to do here now.
      </p>
      <div>
        <Link
          to={`/manager/cases/${caseId}`}
          className="inline-flex items-center rounded-sm border border-border px-4 py-2.5 text-sm font-medium hover:bg-muted"
        >
          Back to {firstName}&rsquo;s support pathway
        </Link>
      </div>
    </Card>
  );
}
